import React, { useState, useRef, useEffect } from "react";
import { Globe, DollarSign, ChevronDown, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useCurrency } from "@/lib/currency";
import { setLanguage } from "@/lib/i18n";

export default function LangCurrencySwitcher({ light = false }) {
    const { i18n } = useTranslation();
    const { currency, currencies, languages, setCurrencyCode } = useCurrency();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const onClickOutside = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onClickOutside);
        return () => document.removeEventListener("mousedown", onClickOutside);
    }, []);

    const lng = (i18n.language || "tr").slice(0, 2);
    const activeLang = (languages.find((l) => l.code === lng) || languages.find((l) => l.code === "tr") || { code: "tr", flag: "🇹🇷", name: "Türkçe" });
    const showCurrency = (currencies || []).length > 1;

    const btnText = light ? "text-white" : "text-slate-700 dark:text-slate-200";
    const btnBg = light ? "hover:bg-white/10" : "hover:bg-slate-100 dark:hover:bg-slate-800";

    return (
        <div ref={ref} className="relative">
            <button
                data-testid="lang-currency-toggle"
                onClick={() => setOpen((v) => !v)}
                className={`flex items-center gap-1.5 px-2.5 py-2 rounded-full text-sm font-medium ${btnText} ${btnBg}`}
            >
                <span className="text-base leading-none">{activeLang.flag}</span>
                <span className="hidden lg:inline uppercase tracking-wide text-xs">{activeLang.code}</span>
                {showCurrency && (
                    <>
                        <span className="text-slate-400">·</span>
                        <span className="text-xs font-bold">{currency?.code || "TRY"}</span>
                    </>
                )}
                <ChevronDown className="h-3.5 w-3.5 opacity-70" />
            </button>

            {open && (
                <div data-testid="lang-currency-menu" className="absolute right-0 mt-2 w-64 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xl overflow-hidden z-40">
                    <div className="px-4 py-2 text-[10px] font-bold uppercase tracking-widest text-slate-500 bg-slate-50 dark:bg-slate-800/60 flex items-center gap-1.5">
                        <Globe className="h-3 w-3" /> Dil
                    </div>
                    {languages.map((lng) => {
                        const active = activeLang.code === lng.code;
                        return (
                            <button
                                key={lng.code}
                                data-testid={`lang-opt-${lng.code}`}
                                onClick={() => { setLanguage(lng.code); setOpen(false); }}
                                className={`w-full flex items-center gap-2.5 px-4 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-800 ${active ? "font-bold text-sky-600" : "text-slate-700 dark:text-slate-200"}`}
                            >
                                <span className="text-base leading-none">{lng.flag}</span>
                                <span className="flex-1 text-left">{lng.name}</span>
                                {active && <Check className="h-4 w-4 text-sky-500" />}
                            </button>
                        );
                    })}

                    <div className="px-4 py-2 text-[10px] font-bold uppercase tracking-widest text-slate-500 bg-slate-50 dark:bg-slate-800/60 flex items-center gap-1.5 border-t border-slate-100 dark:border-slate-800" style={{ display: showCurrency ? "flex" : "none" }}>
                        <DollarSign className="h-3 w-3" /> Para Birimi
                    </div>
                    {showCurrency && currencies.map((c) => {
                        const active = currency?.code === c.code;
                        return (
                            <button
                                key={c.code}
                                data-testid={`currency-opt-${c.code}`}
                                onClick={() => { setCurrencyCode(c.code); setOpen(false); }}
                                className={`w-full flex items-center gap-2.5 px-4 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-800 ${active ? "font-bold text-sky-600" : "text-slate-700 dark:text-slate-200"}`}
                            >
                                <span className="w-5 text-center text-base leading-none">{c.symbol}</span>
                                <span className="flex-1 text-left">{c.code}</span>
                                {active && <Check className="h-4 w-4 text-sky-500" />}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
